import * as dagre from '@dagrejs/dagre'
import { MarkerType, type Node, type Edge } from '@vue-flow/core'
import { formatOwnedAgentRef, findAgentItemBySelector } from '@/utils/agentRefs'
import { buildWorkflowHelperRegex } from '@/utils/workflowHelpers'
import type { AccessibleAgentItem } from '@/store/agents'
import type { WorkflowConfig } from '@/types/workflows'
import type { AgentConfig } from '@/types/agents'

// Builds the VueFlow node/edge graph for a workflow: the orchestrator (main) agent, one node per
// helper-token occurrence, and each agent's tools / embedding collections, laid out left-to-right
// with dagre. Pure — given a workflow config and the accessible-agent catalog it returns the graph,
// so both the Advanced WorkflowEditor and the simple-mode Automations visual view render identically.

const AGENT_NODE_WIDTH = 200
const AGENT_NODE_HEIGHT = 50
const COMPONENT_NODE_WIDTH = 150
const COMPONENT_NODE_HEIGHT = 40

function addAgentComponentsToGraph(
  agent: AgentConfig,
  agentNodeId: string,
  nodes: Node[],
  edges: Edge[],
  g: dagre.graphlib.Graph,
  instanceSuffix = 'default'
) {
  agent.toolOptions?.userTools?.forEach((toolName) => {
    const toolNodeId = `tool-${agent.id}-${instanceSuffix}-${toolName}`
    nodes.push({
      id: toolNodeId,
      type: 'tool',
      label: `Tool: ${toolName}`,
      data: { ownerAgentId: agent.id, ownerAgentName: agent.name, nodeKind: 'tool', toolName },
      position: { x: 0, y: 0 }
    })
    g.setNode(toolNodeId, { width: COMPONENT_NODE_WIDTH, height: COMPONENT_NODE_HEIGHT })
    edges.push({ id: `e-${toolNodeId}-${agentNodeId}`, source: toolNodeId, target: agentNodeId, markerEnd: MarkerType.ArrowClosed })
    g.setEdge(toolNodeId, agentNodeId)
  })

  agent.embeddingOptions?.collections?.forEach((collectionName) => {
    const collectionNodeId = `collection-${agent.id}-${instanceSuffix}-${collectionName}`
    nodes.push({
      id: collectionNodeId,
      type: 'embeddingCollection',
      label: `DB: ${collectionName}`,
      data: { ownerAgentId: agent.id, ownerAgentName: agent.name, nodeKind: 'embeddingCollection', collectionName },
      position: { x: 0, y: 0 }
    })
    g.setNode(collectionNodeId, { width: COMPONENT_NODE_WIDTH, height: COMPONENT_NODE_HEIGHT })
    edges.push({ id: `e-${collectionNodeId}-${agentNodeId}`, source: collectionNodeId, target: agentNodeId, markerEnd: MarkerType.ArrowClosed })
    g.setEdge(collectionNodeId, agentNodeId)
  })
}

export function buildWorkflowGraph(
  config: WorkflowConfig,
  agents: AccessibleAgentItem[]
): { nodes: Node[]; edges: Edge[] } {
  const newNodes: Node[] = []
  const newEdges: Edge[] = []
  const g = new dagre.graphlib.Graph()
  g.setGraph({ rankdir: 'LR', ranksep: 100, nodesep: 50 })
  g.setDefaultEdgeLabel(() => ({}))

  // 1. Main (orchestrator) agent and its components
  const effectiveMainAgentRef =
    config.mainAgentRef ||
    (typeof config.mainAgentId === 'string' && config.mainAgentId.length > 0
      ? formatOwnedAgentRef(config.mainAgentId)
      : null)
  if (effectiveMainAgentRef) {
    const mainAgentItem = findAgentItemBySelector(effectiveMainAgentRef, agents)
    if (mainAgentItem?.kind === 'owned' && mainAgentItem.ownedConfig) {
      const agent = mainAgentItem.ownedConfig.config
      const agentNodeId = `agent-${mainAgentItem.ref}`
      newNodes.push({
        id: agentNodeId,
        type: 'mainAgent',
        label: agent.name,
        data: { agentRef: mainAgentItem.ref, agentId: agent.id, agentName: agent.name, ownerAgentId: agent.id, ownerAgentName: agent.name, nodeKind: 'agent' },
        position: { x: 0, y: 0 }
      })
      g.setNode(agentNodeId, { width: AGENT_NODE_WIDTH, height: AGENT_NODE_HEIGHT })
      addAgentComponentsToGraph(agent, agentNodeId, newNodes, newEdges, g)
    } else if (mainAgentItem?.kind === 'shared' && mainAgentItem.sharedAgent) {
      const agentNodeId = `agent-${mainAgentItem.ref}`
      newNodes.push({
        id: agentNodeId,
        type: 'mainAgent',
        label: mainAgentItem.title,
        data: {
          agentRef: mainAgentItem.ref,
          agentId: mainAgentItem.agentId || '',
          agentName: mainAgentItem.title,
          ownerAgentId: mainAgentItem.agentId || '',
          ownerAgentName: mainAgentItem.title,
          sharedAgent: mainAgentItem.sharedAgent,
          nodeKind: 'agent'
        },
        position: { x: 0, y: 0 }
      })
      g.setNode(agentNodeId, { width: AGENT_NODE_WIDTH, height: AGENT_NODE_HEIGHT })
    }
  }

  // 2. Helper agents (one node per token occurrence) and their components
  const helperRegex = buildWorkflowHelperRegex(config.delimiter)
  let match: RegExpExecArray | null
  const helperOccurrences = new Map<string, number>()

  while ((match = helperRegex.exec(config.mainPrompt)) !== null) {
    const [, agentName] = match
    const helperAgentItem = findAgentItemBySelector(agentName, agents)

    if (helperAgentItem?.kind === 'owned' && helperAgentItem.ownedConfig) {
      const agent = helperAgentItem.ownedConfig.config
      const occurrence = (helperOccurrences.get(agentName) || 0) + 1
      helperOccurrences.set(agentName, occurrence)
      const agentNodeId = `agent-${helperAgentItem.ref}-helper-${occurrence}`
      const helperLabel = occurrence > 1 ? `${agent.name} #${occurrence}` : agent.name
      newNodes.push({
        id: agentNodeId,
        type: 'helperAgent',
        label: helperLabel,
        data: { agentRef: helperAgentItem.ref, agentId: agent.id, agentName: agent.name, ownerAgentId: agent.id, ownerAgentName: agent.name, occurrence, nodeKind: 'agent' },
        position: { x: 0, y: 0 }
      })
      g.setNode(agentNodeId, { width: AGENT_NODE_WIDTH, height: AGENT_NODE_HEIGHT })
      addAgentComponentsToGraph(agent, agentNodeId, newNodes, newEdges, g, `helper-${occurrence}`)

      if (effectiveMainAgentRef) {
        const mainAgentNodeId = `agent-${effectiveMainAgentRef}`
        newEdges.push({ id: `e-${agentNodeId}-${mainAgentNodeId}`, source: agentNodeId, target: mainAgentNodeId, markerEnd: MarkerType.ArrowClosed })
        g.setEdge(agentNodeId, mainAgentNodeId)
      }
    } else if (helperAgentItem?.kind === 'shared' && helperAgentItem.sharedAgent) {
      const occurrence = (helperOccurrences.get(agentName) || 0) + 1
      helperOccurrences.set(agentName, occurrence)
      const agentNodeId = `agent-${helperAgentItem.ref}-helper-${occurrence}`
      const helperLabel = occurrence > 1 ? `${helperAgentItem.title} #${occurrence}` : helperAgentItem.title
      newNodes.push({
        id: agentNodeId,
        type: 'helperAgent',
        label: helperLabel,
        data: {
          agentRef: helperAgentItem.ref,
          agentId: helperAgentItem.agentId || '',
          agentName: helperAgentItem.title,
          ownerAgentId: helperAgentItem.agentId || '',
          ownerAgentName: helperAgentItem.title,
          sharedAgent: helperAgentItem.sharedAgent,
          occurrence,
          nodeKind: 'agent'
        },
        position: { x: 0, y: 0 }
      })
      g.setNode(agentNodeId, { width: AGENT_NODE_WIDTH, height: AGENT_NODE_HEIGHT })

      if (effectiveMainAgentRef) {
        const mainAgentNodeId = `agent-${effectiveMainAgentRef}`
        newEdges.push({ id: `e-${agentNodeId}-${mainAgentNodeId}`, source: agentNodeId, target: mainAgentNodeId, markerEnd: MarkerType.ArrowClosed })
        g.setEdge(agentNodeId, mainAgentNodeId)
      }
    }
  }

  // 3. Layout, then map dagre centers back to top-left positions VueFlow expects. (Matches the
  // Advanced editor, which offsets every node by the agent-node dimensions.)
  dagre.layout(g)
  const nodes = newNodes.map((node) => {
    const positioned = g.node(node.id)
    return { ...node, position: { x: positioned.x - AGENT_NODE_WIDTH / 2, y: positioned.y - AGENT_NODE_HEIGHT / 2 } }
  })

  return { nodes, edges: newEdges }
}
